import { Observable, Subject, Subscription, filter, first, of } from 'rxjs';
import { getCurrentTime, hasValidFileExtension } from 'src/app/common/app.helpers';
import AppValues from 'src/app/common/app.values';
import FieldsConstants from 'src/app/common/fields.constants';
import { FileManagerService } from 'src/app/components/files/file-manager.service';
import { DocumentContent, ImageDimension, OnFileUploaded } from 'src/app/interfaces/file.interface';
import { FileProcessingContent, LoadFileContent } from 'src/app/interfaces/form-and-field.interface';
import { NotificationContextAttributesService } from 'src/app/notifications/notifications-context-attributes.service';
import { NotificationService } from 'src/app/notifications/notifications.service';
import { MaybeUndefined } from 'src/app/utils';

export interface FileUploadStrategy {
  uploadFile(data: LoadFileContent, files: File[]): void;
}

export abstract class SingleFileUpload implements FileUploadStrategy {
  public constructor(
    private onError$: Subject<OnFileUploaded>,
    private fileManagerService: FileManagerService,
    protected notificationService: NotificationService,
    protected modalAttributesService: NotificationContextAttributesService,
  ) {}

  /**
   * @description Checks file's size and extension.
   *              In case invalid data - emits error event.
   * @param { LoadFileContent } data Contains upload event, control name and type.
   * @param { File[] } files Files to upload.
   */
  public uploadFile(data: LoadFileContent, files: File[]): void {
    const file: MaybeUndefined<File> = files[0];

    if (!file) {
      return;
    }

    const content: DocumentContent = {
      fileContent: '',
      type: data.attr.backendAttribute.name,
    };

    if (!this.hasValidFileSize(file.size) || !hasValidFileExtension(file, FieldsConstants.ALLOWED_IMAGES_EXTENSIONS)) {
      this.handleInvalidFileUpload({
        link: '',
        controlName: data.attr.backendAttribute.name,
        fileName: '',
      });
      return;
    }

    this.processFile({
      file,
      fileType: data.attr.backendAttribute.value_class_front,
      content,
      enableCrop: data.enableCrop,
    });
  }

  /**
   * @description Checks file's dimensions by its type.
   * @param { ImageDimension } dimensions
   * @param { string } type Control type. Can be 'photo' or 'file'.
   */
  protected abstract hasInvalidFileDimensions(dimensions: ImageDimension): boolean;

  /**
   * @description Checks file type error and displays custom modal with description.
   * @param { error } OnFileUploaded
   */
  protected handleInvalidFileUpload(error: OnFileUploaded): void {
    this.onError$.next(error);
  }

  /**
   * @description Reads file's data as URL and calls onImageLoad().
   * @param { FileProcessingContent } data Contains upload event, control name and type.
   */
  private processFile(data: FileProcessingContent): void {
    const reader: FileReader = new FileReader();
    reader.onload = (e: ProgressEvent<FileReader>) => {
      const image: HTMLImageElement = new Image();
      const result: string | ArrayBuffer | null | undefined = e.target?.result;

      if (!result) {
        return;
      }

      if (typeof result !== 'string') {
        return;
      }

      image.src = result;

      image.onload = () => {
        this.onImageLoad(data, {
          height: image.height,
          width: image.height,
        });
      };
    };

    reader.readAsDataURL(data.file);
  }

  /**
   * @description Checks file's dimensions and calls generateUploadUrl().
   *              In case invalid dimensions - emits error event.
   * @param { FileProcessingContent } data Contains upload event, control name and type.
   */
  private onImageLoad(data: FileProcessingContent, dimensions: ImageDimension): void {
    if (this.hasInvalidFileDimensions({ height: dimensions.height, width: dimensions.width })) {
      this.handleInvalidFileUpload({ link: '', controlName: data.content.type, fileName: '' });
      return;
    }

    const file$: Observable<File> = this.isShowCrop(data)
      ? this.notificationService.dialogCrop({ file: data.file }).pipe(first())
      : of(data.file);

    file$.pipe(filter((file: File) => Boolean(file))).subscribe((file: File) => {
      this.fileManagerService.generateUploadUrl({ file, public: true, controlName: data.content.type });
    });
  }

  private isShowCrop(data: FileProcessingContent): boolean {
    return data.file.type !== FieldsConstants.IMAGE_GIF_FILE && data.enableCrop;
  }

  /**
   * @description Checks file's size.
   * @param { number } fileSize
   */
  private hasValidFileSize(fileSize: number): boolean {
    return fileSize <= FieldsConstants.MAX_FILE_SIZE * AppValues.valueMB;
  }
}

export class PhotoUpload extends SingleFileUpload {
  protected hasInvalidFileDimensions(dimensions: ImageDimension): boolean {
    return dimensions.height < FieldsConstants.MIN_IMAGE_HEIGHT || dimensions.width < FieldsConstants.MIN_IMAGE_WIDTH;
  }
}

export class DocumentUpload extends SingleFileUpload {
  protected hasInvalidFileDimensions(dimensions: ImageDimension): boolean {
    return dimensions.height < FieldsConstants.MIN_FILE_HEIGHT || dimensions.width < FieldsConstants.MIN_FILE_WIDTH;
  }
}

export class RichEditorUpload extends SingleFileUpload {
  protected hasInvalidFileDimensions(dimensions: ImageDimension): boolean {
    return dimensions.height < FieldsConstants.MIN_IMAGE_HEIGHT || dimensions.width < FieldsConstants.MIN_IMAGE_WIDTH;
  }

  protected override handleInvalidFileUpload(error: OnFileUploaded): void {
    super.handleInvalidFileUpload(error);

    const subscription: Subscription = this.notificationService
      .dialogDefault({
        title: 'RICH_TEXT_EDITOR_INVALID_IMG_TITLE',
        description: 'RICH_TEXT_EDITOR_INVALID_IMG_BODY',
        buttons: this.modalAttributesService.getDefaultCloseAttr(),
      })
      .subscribe(() => {
        subscription.unsubscribe();
      });
  }
}

export class MultipleFileUpload implements FileUploadStrategy {
  public constructor(private fileManagerService: FileManagerService) {}

  /**
   * @description Calls validateBeforeCreateFolder() method with specified data and formed name.
   * @param { LoadFileContent } data Contains upload event, control name and type.
   * @param { File[] } files Files to upload.
   */
  public uploadFile(data: LoadFileContent, files: File[]): void {
    const name = `${FieldsConstants.CONTACT_US_FOLDER_SUFFIX_NAME}-${getCurrentTime()}`;
    this.fileManagerService.validateBeforeCreateFolder(files, data.attr.backendAttribute.name, name);
  }
}

export const SingleFileUploadLookup: Record<
  PropertyKey,
  typeof PhotoUpload | typeof DocumentUpload | typeof RichEditorUpload
> = {
  photo: PhotoUpload,
  file: DocumentUpload,
  rich_editor: RichEditorUpload,
};
